import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Loader2, MessageSquare, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import StudioNewThreadDialog, {
  type StudioNewThreadDraft,
} from "@/components/studio/StudioNewThreadDialog";
import {
  STUDIO_TREE_THREAD_PREVIEW,
  formatStudioThreadOptionLabel,
  studioThreadTreeBusy,
  visibleStudioTreeThreads,
} from "@/components/studio/studioThreadUtils";
import { useAgentSpecStudioThreads } from "@/hooks/useAgentSpecStudioThreads";
import { useAgentSpecStudioStore } from "@/store/agentSpecStudio";
import { useContextStore } from "@/store/context";
import { cn } from "@/utils/cn";
import { studioNodeImpliesGlobalScope } from "@/utils/instanceRole";
import { lockLeafNavigation } from "@/utils/leafNavigationLock";

export type AgentSpecStudioThreadsSidebarHandle = {
  startNewThread: () => void;
};

/** Studio session threads listed under a single Agent. */
const AgentSpecStudioThreadsSidebar = forwardRef<
  AgentSpecStudioThreadsSidebarHandle,
  {
    expanded: boolean;
    agentId: string;
    hideNewThreadRow?: boolean;
    onEnterStudio?: (agentId: string, applicationKey?: string) => void;
    onNavigate?: () => void;
  }
>(function AgentSpecStudioThreadsSidebar(
  { expanded, agentId, hideNewThreadRow = false, onEnterStudio, onNavigate },
  ref,
) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showOlderThreads, setShowOlderThreads] = useState(false);
  useEffect(() => {
    setShowOlderThreads(false);
  }, [agentId]);
  const chiefOfStaff = useContextStore((state) => state.chiefOfStaff);
  const environmentName = useContextStore((state) => state.env);
  const impliedGlobalScope = studioNodeImpliesGlobalScope(
    agentId,
    chiefOfStaff?.id,
  );
  const {
    canWriteRecipes,
    organizationId,
    threads,
    threadsQuery,
    selectedThreadId,
    createThread,
    createThreadPending,
    createFromGoalPending,
    selectThread,
    listError,
  } = useAgentSpecStudioThreads({ agentId });
  const turnPending = useAgentSpecStudioStore((state) => state.turnPending);
  const createBusy =
    createThreadPending || createFromGoalPending || threadsQuery.isLoading;

  if (!expanded) return null;
  if (!canWriteRecipes || !organizationId || !agentId) return null;

  const { visible: visibleThreads, hiddenCount } = visibleStudioTreeThreads(
    threads,
    {
      expanded: showOlderThreads,
      selectedThreadId,
    },
  );

  const openThread = (threadId: string) => {
    lockLeafNavigation();
    onEnterStudio?.(agentId);
    selectThread(threadId);
    onNavigate?.();
  };

  const submitDraft = (draft: StudioNewThreadDraft) => {
    onEnterStudio?.(draft.agentId, draft.applicationKey);
    createThread({
      hostAgentId: draft.agentId,
      environmentName: draft.environmentName,
      applicationKey: impliedGlobalScope ? undefined : draft.applicationKey,
      goal: draft.goalTitle
        ? { title: draft.goalTitle, description: draft.goalDescription }
        : undefined,
    });
    setDialogOpen(false);
    onNavigate?.();
  };

  const startNewThread = useCallback(() => {
    if (impliedGlobalScope) {
      onEnterStudio?.(agentId);
      createThread({
        hostAgentId: agentId,
        environmentName: environmentName ?? undefined,
      });
      onNavigate?.();
      return;
    }
    setDialogOpen(true);
  }, [
    agentId,
    createThread,
    environmentName,
    impliedGlobalScope,
    onEnterStudio,
    onNavigate,
  ]);
  useImperativeHandle(ref, () => ({ startNewThread }), [startNewThread]);

  return (
    <div
      className="space-y-1"
      data-testid={`shell-nav-studio-agent-${agentId}-studio-threads`}
    >
      {hideNewThreadRow ? null : (
      <button
        type="button"
        data-testid={`shell-nav-studio-agent-${agentId}-new-thread`}
        onClick={startNewThread}
        disabled={createBusy}
        className={cn(
          "flex w-full items-center gap-2 rounded-md py-1.5 text-left text-sm transition-colors",
          "text-foreground hover:bg-card-raised hover:text-foreground",
          createBusy && "pointer-events-none opacity-60",
        )}
      >
        {createThreadPending ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" />
        ) : (
          <Plus className="h-4 w-4 shrink-0" />
        )}
        <span className="truncate">
          {t(
            createThreadPending
              ? "agentSpec.studioCreatingThread"
              : "agentSpec.studioCreateThread",
          )}
        </span>
      </button>
      )}

      <StudioNewThreadDialog
        open={dialogOpen}
        pending={createBusy}
        presetAgentId={agentId}
        lockAgent
        onOpenChange={setDialogOpen}
        onSubmit={submitDraft}
      />

      {threadsQuery.isLoading ? (
        <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
          {t("agentSpec.studioThreadsLoading")}
        </div>
      ) : null}

      {!threadsQuery.isLoading && threads.length === 0 ? (
        <p className="px-2 py-1 text-xs text-muted-foreground">
          {t("agentSpec.studioNoThreadYet")}
        </p>
      ) : null}

      {listError ? (
        <p className="px-2 py-1 text-xs text-destructive" role="alert">
          {listError}
        </p>
      ) : null}

      <div className="max-h-64 space-y-0.5 overflow-y-auto">
        {visibleThreads.map((thread) => {
          const active = thread.id === selectedThreadId;
          const label = formatStudioThreadOptionLabel(
            thread,
            threads,
            t("agentSpec.studioUntitledThread"),
          );
          const busy = studioThreadTreeBusy({
            threadId: thread.id,
            attention: thread.attention,
            selectedThreadId,
            turnPending,
          });
          const needsAttention = Boolean(thread.attention) && !busy;
          const subtitle = thread.last_line || thread.goal?.title || "";
          const attentionLabel =
            thread.attention === "needs_approval"
              ? t("agentSpec.studioAttentionNeedsApproval")
              : thread.attention === "blocked"
                ? t("agentSpec.studioAttentionBlocked")
                : thread.attention === "running"
                  ? t("agentSpec.studioAttentionRunning")
                  : thread.attention;
          return (
            <button
              key={thread.id}
              type="button"
              data-testid={`shell-nav-studio-calisto-thread-${thread.id}`}
              onClick={() => openThread(thread.id)}
              aria-current={active ? "true" : undefined}
              aria-busy={busy || undefined}
              title={subtitle || label}
              className={cn(
                "flex w-full items-center gap-2 rounded-md py-1.5 text-left text-sm transition-colors",
                active
                  ? "bg-primary-soft font-semibold text-foreground"
                  : "text-foreground hover:bg-card-raised hover:text-foreground",
              )}
            >
              {busy ? (
                <Loader2
                  data-testid={`shell-nav-studio-thread-busy-${thread.id}`}
                  className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none"
                />
              ) : (
                <MessageSquare className="h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate">{label}</span>
                {subtitle ? (
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {subtitle}
                  </span>
                ) : null}
              </span>
              {needsAttention ? (
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning"
                  aria-label={attentionLabel}
                />
              ) : null}
            </button>
          );
        })}
        {hiddenCount > 0 ||
        (showOlderThreads && threads.length > STUDIO_TREE_THREAD_PREVIEW) ? (
          <button
            type="button"
            data-testid={`shell-nav-studio-agent-${agentId}-threads-${
              showOlderThreads ? "less" : "more"
            }`}
            aria-expanded={showOlderThreads}
            onClick={() => setShowOlderThreads((current) => !current)}
            className="flex w-full items-center gap-2 rounded-md py-1.5 text-left text-sm text-muted-foreground hover:bg-card-raised hover:text-foreground"
          >
            <span className="inline-block h-4 w-4 shrink-0" />
            <span className="truncate">
              {t(
                showOlderThreads
                  ? "agentSpec.studioThreadsLess"
                  : "agentSpec.studioThreadsMore",
              )}
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
});

export default AgentSpecStudioThreadsSidebar;
